import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  HeartIcon as HeartIconSolid,
  ShoppingCartIcon,
  TrashIcon,
  ShareIcon
} from '@heroicons/react/24/solid';
import { HeartIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import toast from 'react-hot-toast';

const WishlistPage = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/wishlist');
      setWishlistItems(response.data.wishlist || []);
    } catch (error) {
      setError('שגיאה בטעינת רשימת המשאלות');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`/api/wishlist/${productId}`);
      setWishlistItems(prev => prev.filter(item => item.product.id !== productId));
      toast.success('המוצר הוסר מרשימת המשאלות');
    } catch (error) {
      toast.error('שגיאה בהסרת המוצר מרשימת המשאלות');
    }
  };

  const moveToCart = async (item) => {
    try {
      await addToCart(item.product, 1);
      await removeFromWishlist(item.product.id);
      toast.success('המוצר הועבר לעגלה');
    } catch (error) {
      toast.error('שגיאה בהעברת המוצר לעגלה');
    }
  };

  const clearWishlist = async () => {
    if (window.confirm('האם אתה בטוח שברצונך לנקות את כל רשימת המשאלות?')) {
      try {
        await axios.delete('/api/wishlist');
        setWishlistItems([]);
        toast.success('רשימת המשאלות נוקתה');
      } catch (error) {
        toast.error('שגיאה בניקוי רשימת המשאלות');
      }
    }
  };

  const shareWishlist = async () => {
    const wishlistText = wishlistItems.map(item => 
      `${item.product.name} - ${formatPrice(getCurrentPrice(item.product))}`
    ).join('\n');
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'רשימת המשאלות שלי',
          text: wishlistText,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(wishlistText);
      toast.success('רשימת המשאלות הועתקה ללוח');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(price);
  };

  const getCurrentPrice = (product) => {
    if (user?.role === 'company_admin' || user?.role === 'company_user') {
      return parseFloat(product.price_b2b) || parseFloat(product.price_b2c) || 0;
    }
    return parseFloat(product.price_b2c) || 0;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center px-4">
            <HeartIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-gray-900">רשימת המשאלות</h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600">עליך להתחבר כדי לצפות ברשימת המשאלות שלך</p>
            <div className="mt-6">
              <Link
                to="/login"
                className="btn btn-primary touch-manipulation"
              >
                התחבר
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-sm sm:text-base text-gray-600">טוען רשימת משאלות...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center px-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">שגיאה</h1>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">{error}</p>
            <button
              onClick={fetchWishlist}
              className="btn btn-primary touch-manipulation"
            >
              נסה שוב
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center px-4">
            <HeartIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-gray-900">רשימת המשאלות שלך</h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600">רשימת המשאלות שלך ריקה כרגע</p>
            <p className="text-sm sm:text-base text-gray-500">הוסף מוצרים שאתה אוהב כדי לשמור אותם למועד מאוחר יותר</p>
            <div className="mt-6">
              <Link
                to="/products"
                className="btn btn-primary touch-manipulation"
              >
                התחל לקנות
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header - Mobile First */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <HeartIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-red-500 flex-shrink-0" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">רשימת המשאלות שלי</h1>
                <p className="text-sm sm:text-base text-gray-600">{wishlistItems.length} מוצרים</p>
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={shareWishlist}
                className="flex-1 sm:flex-initial btn btn-outline flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-xs sm:text-sm touch-manipulation"
              >
                <ShareIcon className="h-4 w-4" />
                <span>שתף</span>
              </button>
              {wishlistItems.length > 0 && (
                <button
                  onClick={clearWishlist}
                  className="flex-1 sm:flex-initial btn btn-outline text-red-600 border-red-300 hover:bg-red-50 active:bg-red-100 px-3 sm:px-4 py-2.5 sm:py-2 text-xs sm:text-sm touch-manipulation"
                >
                  נקה הכל
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Wishlist Items - Mobile First Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
          {wishlistItems.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm overflow-hidden group hover:shadow-md transition-shadow">
              {/* Product Image */}
              <div className="aspect-square bg-gray-100 relative overflow-hidden">
                <Link to={`/products/${item.product.id}`}>
                  {item.product.images && item.product.images.length > 0 ? (
                    <img
                      src={item.product.images[0]}
                      alt={item.product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-300 rounded-lg"></div>
                    </div>
                  )}
                </Link>
                
                {/* Remove from wishlist button - Always visible on mobile */}
                <button
                  onClick={() => removeFromWishlist(item.product.id)}
                  className="absolute top-2 right-2 p-2 sm:p-2.5 bg-white/90 hover:bg-white active:bg-red-50 rounded-full shadow-md sm:opacity-0 sm:group-hover:opacity-100 transition-all touch-manipulation"
                  title="הסר מרשימת המשאלות"
                  aria-label="הסר מרשימת המשאלות"
                >
                  <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
                </button>

                {/* Stock status */}
                <div className="absolute bottom-2 left-2">
                  {item.product.stock_quantity > 0 ? (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      במלאי
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                      אזל
                    </span>
                  )}
                </div>
              </div>

              {/* Product Info - Mobile Optimized */}
              <div className="p-3 sm:p-4">
                <Link to={`/products/${item.product.id}`}>
                  <h3 className="font-medium text-gray-900 hover:text-primary-600 transition-colors line-clamp-2 mb-1.5 sm:mb-2 text-sm sm:text-base min-h-[2.5rem] sm:min-h-[3rem]">
                    {item.product.name}
                  </h3>
                </Link>

                {/* Price - Responsive */}
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className="text-base sm:text-lg font-bold text-primary-600">
                    {formatPrice(getCurrentPrice(item.product))}
                  </span>
                  {user?.role === 'company_admin' || user?.role === 'company_user' ? (
                    <span className="text-xs font-medium text-blue-600">B2B</span>
                  ) : (
                    <span className="text-xs text-gray-500 hidden sm:inline">כולל מע"מ</span>
                  )}
                </div>

                {/* SKU - Hide on very small screens */}
                {item.product.sku && (
                  <p className="hidden sm:block text-xs text-gray-500 mb-2 sm:mb-3">מק"ט: {item.product.sku}</p>
                )}

                {/* Added date - Hide on mobile */}
                <p className="hidden sm:block text-xs text-gray-400 mb-3 sm:mb-4">
                  נוסף ב-{new Date(item.created_at).toLocaleDateString('he-IL')}
                </p>

                {/* Action button - Mobile Optimized */}
                <button
                  onClick={() => moveToCart(item)}
                  disabled={item.product.stock_quantity === 0}
                  className="w-full bg-primary-600 text-white px-3 py-2.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 touch-manipulation transition-colors"
                >
                  <ShoppingCartIcon className="h-4 w-4" />
                  <span>הוסף לעגלה</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Continue Shopping - Mobile Optimized */}
        <div className="mt-8 sm:mt-12 text-center">
          <Link
            to="/products"
            className="btn btn-outline touch-manipulation"
          >
            המשך לקנות
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WishlistPage;
